import { apiUrl } from "@/lib/api-base";

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
}

export interface RawAuthUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  displayName: string | null;
  journalingGoal: string | null;
  onboardingCompleted: boolean;
  [key: string]: unknown;
}

const STORAGE_KEY = "unfold_auth_session";
// Refresh this long before actual expiry so a request never races the token dying mid-flight.
const REFRESH_SKEW_MS = 60_000;

export function getSession(): StoredSession | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.accessToken === "string" && typeof parsed?.refreshToken === "string") {
      return parsed;
    }
  } catch {
    // fall through
  }
  return null;
}

export function setSession(session: StoredSession): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/** Clears the local session and notifies any code (e.g. AuthProvider) that isn't the one that noticed the failure. */
export function clearSession(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("unfold:session-cleared"));
}

/** Reads the `exp` claim from a JWT without verifying it — only used to schedule proactive refresh, never trusted for authorization (the server always re-verifies). */
function getTokenExpiryMs(jwt: string): number | null {
  try {
    const payload = jwt.split(".")[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

type AuthEndpointResult<T> = { ok: true; data: T } | { ok: false; status: number; error: string };

async function postAuthEndpoint<T>(path: string, body: unknown): Promise<AuthEndpointResult<T>> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        error: (data as { error?: string } | null)?.error ?? "Something went wrong. Please try again.",
      };
    }
    return { ok: true, data: data as T };
  } catch {
    return { ok: false, status: 0, error: "Network error — please check your connection and try again." };
  }
}

export function apiSignup(email: string, password: string, firstName: string, lastName: string) {
  return postAuthEndpoint<{ ok: true }>("/auth/signup", { email, password, firstName, lastName });
}

export function apiLogin(email: string, password: string) {
  return postAuthEndpoint<{ accessToken: string; refreshToken: string; user: RawAuthUser }>("/auth/login", {
    email,
    password,
  });
}

function apiRefresh(refreshToken: string) {
  return postAuthEndpoint<StoredSession>("/auth/refresh", { refreshToken });
}

/** Best-effort server-side revoke — the local session is cleared by the caller regardless of whether this succeeds. */
export async function apiLogout(refreshToken: string): Promise<void> {
  try {
    await fetch(apiUrl("/auth/logout"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // network failure here shouldn't block local sign-out
  }
}

let refreshPromise: Promise<StoredSession | null> | null = null;

/** Concurrent callers share one in-flight refresh — the refresh token rotates on use, so two parallel refreshes would have the second fail against an already-revoked token. */
function refreshAndPersist(current: StoredSession): Promise<StoredSession | null> {
  if (!refreshPromise) {
    refreshPromise = apiRefresh(current.refreshToken)
      .then((result) => {
        if (result.ok) {
          setSession(result.data);
          return result.data;
        }
        clearSession();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Returns a currently-valid access token for API calls, transparently refreshing
 * first when it's missing, expired, or about to expire. Returns null when there's
 * no session or the refresh itself fails (session is cleared in that case).
 */
export async function getValidAccessToken(): Promise<string | null> {
  const current = getSession();
  if (!current) return null;

  const expiresAt = getTokenExpiryMs(current.accessToken);
  const needsRefresh = expiresAt === null || expiresAt - Date.now() < REFRESH_SKEW_MS;
  if (!needsRefresh) return current.accessToken;

  const refreshed = await refreshAndPersist(current);
  return refreshed?.accessToken ?? null;
}
